/**
 * MediaPickerModal
 * ─────────────────
 * Reusable modal for browsing, searching, uploading, and selecting media assets.
 *
 * Props:
 *   open      — boolean
 *   onClose   — () => void
 *   onSelect  — ({ url, publicId }) => void   called on image click
 *   title     — string (optional)
 *   folder    — string (optional, passed to API as folder filter)
 */

import { useState, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  X, Search, Upload, Trash2, ImageIcon,
  ChevronRight, ChevronLeft, Check, Loader2,
} from 'lucide-react'
import { mediaService } from '@/services/mediaService'
import { useDebounce } from '@/hooks/useDebounce'
import { imgUrl } from '@/utils/img'

const LIMIT = 24

export default function MediaPickerModal({
  open,
  onClose,
  onSelect,
  title = 'مكتبة الوسائط',
  folder,
}) {
  const qc = useQueryClient()
  const fileInputRef = useRef(null)

  const [page, setPage]             = useState(1)
  const [search, setSearch]         = useState('')
  const [hovered, setHovered]       = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [uploading, setUploading]   = useState(false)

  const debouncedSearch = useDebounce(search, 300)

  const { data, isLoading } = useQuery({
    queryKey: ['media-assets', page, folder],
    queryFn: () => mediaService.listAssets({ page, limit: LIMIT, ...(folder ? { folder } : {}) }),
    enabled: open,
    keepPreviousData: true,
  })

  const assets = data?.assets || []
  const meta   = data?.meta   || {}

  // Client-side search filter by filename
  const filtered = useMemo(() => {
    if (!debouncedSearch.trim()) return assets
    const q = debouncedSearch.toLowerCase()
    return assets.filter((a) =>
      (a.filename || '').toLowerCase().includes(q) ||
      (a.folder   || '').toLowerCase().includes(q)
    )
  }, [assets, debouncedSearch])

  const deleteMut = useMutation({
    mutationFn: (id) => mediaService.deleteAsset(id),
    onSuccess: () => {
      toast.success('تم حذف الصورة')
      qc.invalidateQueries({ queryKey: ['media-assets'] })
      setDeleteTarget(null)
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل الحذف'),
  })

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setUploading(true)
    try {
      await mediaService.uploadAsset(file, folder)
      toast.success('تم رفع الصورة')
      qc.invalidateQueries({ queryKey: ['media-assets'] })
      setPage(1)
    } catch (err) {
      toast.error(err?.response?.data?.message || 'فشل رفع الصورة')
    } finally {
      setUploading(false)
    }
  }

  const handleSelect = (asset) => {
    onSelect({ url: asset.url, publicId: asset.publicId })
    onClose()
  }

  if (!open) return null

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
      }}
    >
      {/* Modal */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 24,
          width: '100%', maxWidth: 860,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 32px 80px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '16px 20px',
          borderBottom: '1px solid #F0F0F0',
          flexShrink: 0,
        }}>
          {/* Title */}
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 900, fontSize: 16, color: '#1A1C1C' }}>
              {title}
            </div>
            {meta.total != null && (
              <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 12, color: '#767683', marginTop: 1 }}>
                {meta.total} صورة
              </div>
            )}
          </div>

          {/* Search */}
          <div style={{ position: 'relative', flex: '0 0 220px' }}>
            <Search size={14} style={{
              position: 'absolute', top: '50%', transform: 'translateY(-50%)',
              right: 10, color: '#767683', pointerEvents: 'none',
            }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحث باسم الملف..."
              style={{
                width: '100%', borderRadius: 10,
                background: '#F0F0F5', border: 'none',
                padding: '8px 32px 8px 12px',
                fontSize: 13, fontFamily: 'Tajawal, sans-serif',
                outline: 'none', color: '#1A1C1C', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Upload button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 10,
              fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13,
              color: '#fff', background: '#000666', border: 'none',
              cursor: uploading ? 'not-allowed' : 'pointer',
              opacity: uploading ? 0.7 : 1,
              boxShadow: '0 4px 12px rgba(0,6,102,0.2)',
              flexShrink: 0,
            }}
          >
            {uploading
              ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              : <Upload size={14} />
            }
            {uploading ? 'جارٍ الرفع...' : 'رفع صورة'}
          </button>

          {/* Close */}
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 10,
              background: '#F0F0F5', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#767683', flexShrink: 0,
            }}
          >
            <X size={16} />
          </button>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>

        {/* ── Grid ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
              <Loader2 size={28} style={{ color: '#000666', animation: 'spin 1s linear infinite' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', padding: '48px 0', gap: 12,
            }}>
              <div style={{
                width: 56, height: 56, borderRadius: 16,
                background: 'rgba(0,6,102,0.06)', color: '#000666',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <ImageIcon size={24} />
              </div>
              <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 14, color: '#767683' }}>
                {search ? 'لا توجد صور مطابقة للبحث' : 'لا توجد صور بعد'}
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 18px', borderRadius: 10, border: 'none',
                  fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13,
                  color: '#fff', background: '#000666', cursor: 'pointer',
                }}
              >
                <Upload size={13} />ارفع أول صورة
              </button>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 10,
            }}>
              {filtered.map((asset) => (
                <div
                  key={asset.id}
                  style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }}
                  onMouseEnter={() => setHovered(asset.id)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => handleSelect(asset)}
                >
                  {/* Image */}
                  <div style={{
                    width: '100%', aspectRatio: '1',
                    background: '#F0F0F5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden',
                  }}>
                    <img
                      src={imgUrl(asset.url)}
                      alt={asset.filename || ''}
                      style={{
                        width: '100%', height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.2s',
                        transform: hovered === asset.id ? 'scale(1.04)' : 'scale(1)',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.nextSibling.style.display = 'flex'
                      }}
                    />
                    <div style={{
                      display: 'none', width: '100%', height: '100%',
                      alignItems: 'center', justifyContent: 'center', color: '#C4C4C4',
                    }}>
                      <ImageIcon size={28} />
                    </div>
                  </div>

                  {/* Hover overlay */}
                  {hovered === asset.id && (
                    <div style={{
                      position: 'absolute', inset: 0,
                      background: 'rgba(0,0,0,0.45)',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}>
                      {/* Select */}
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: '6px 14px', borderRadius: 8,
                        background: '#fff', color: '#000666',
                        fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 12,
                      }}>
                        <Check size={13} />اختيار
                      </div>
                      {/* Delete */}
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(asset) }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          padding: '4px 10px', borderRadius: 7,
                          background: 'rgba(186,26,26,0.85)', color: '#fff',
                          border: 'none', cursor: 'pointer',
                          fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 11,
                        }}
                      >
                        <Trash2 size={11} />حذف
                      </button>
                    </div>
                  )}

                  {/* Filename tooltip */}
                  <div style={{
                    padding: '4px 6px',
                    background: '#F8F8F8',
                    fontFamily: 'Tajawal, sans-serif', fontSize: 10,
                    color: '#767683', whiteSpace: 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {asset.filename || asset.publicId || '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Pagination ── */}
        {meta.totalPages > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            padding: '12px 20px',
            borderTop: '1px solid #F0F0F0',
            flexShrink: 0,
          }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!meta.hasPrev}
              style={{
                width: 32, height: 32, borderRadius: 8, border: 'none',
                background: meta.hasPrev ? 'rgba(0,6,102,0.08)' : '#F3F3F3',
                color: meta.hasPrev ? '#000666' : '#C4C4C4',
                cursor: meta.hasPrev ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <ChevronRight size={14} />
            </button>
            <span style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13, color: '#767683' }}>
              صفحة {page} من {meta.totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={!meta.hasNext}
              style={{
                width: 32, height: 32, borderRadius: 8, border: 'none',
                background: meta.hasNext ? 'rgba(0,6,102,0.08)' : '#F3F3F3',
                color: meta.hasNext ? '#000666' : '#C4C4C4',
                cursor: meta.hasNext ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <ChevronLeft size={14} />
            </button>
          </div>
        )}
      </div>

      {/* ── Delete confirm ── */}
      {deleteTarget && (
        <div
          onClick={() => setDeleteTarget(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 110,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 20, padding: 28,
              width: '100%', maxWidth: 380, textAlign: 'center',
              boxShadow: '0 24px 60px rgba(0,0,0,0.2)',
            }}
          >
            {/* Preview */}
            <div style={{
              width: 80, height: 80, borderRadius: 14, overflow: 'hidden',
              margin: '0 auto 14px', background: '#F0F0F5',
            }}>
              <img
                src={imgUrl(deleteTarget.url)}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 900, fontSize: 17, color: '#1A1C1C', marginBottom: 6 }}>
              حذف الصورة
            </div>
            <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13, color: '#767683', marginBottom: 20, wordBreak: 'break-all' }}>
              {deleteTarget.filename || deleteTarget.publicId}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => deleteMut.mutate(deleteTarget.id)}
                disabled={deleteMut.isPending}
                style={{
                  flex: 1, padding: '11px 0', borderRadius: 12, border: 'none',
                  fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 14,
                  color: '#fff', background: '#BA1A1A', cursor: 'pointer',
                  opacity: deleteMut.isPending ? 0.6 : 1,
                }}
              >
                {deleteMut.isPending ? '...' : 'حذف'}
              </button>
              <button
                onClick={() => setDeleteTarget(null)}
                style={{
                  flex: 1, padding: '11px 0', borderRadius: 12, border: 'none',
                  fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 14,
                  background: '#F3F3F3', color: '#454652', cursor: 'pointer',
                }}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spinner keyframes */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
